import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Grid,
  Stack
} from '@mui/material';
import { Search, AccountBalance } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import LoadingAnimation from '../components/LoadingAnimation';
import PasswordVerificationModal from '../components/PasswordVerificationModal';
import UserAvatar from '../components/UserAvatar';
import { authService } from '../services/authService';
import { useHRAccess } from '../hooks/useHRAccess';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import { alpha, useTheme } from '@mui/material/styles';

interface Employee {
  id: number;
  name: string;
  mail: string;
  department: string;
  user_office: string;
  paid_leave_balance: number;
  rnr_balance: number;
  paid_leave_per_month: number | null;
  roles: string[];
  profile_picture?: string;
}

const HRManagementPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  // Nautilus Console Design System tokens
  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [passwordVerificationOpen, setPasswordVerificationOpen] = useState(false);
  const { isVerified: passwordVerified, grantAccess } = useHRAccess();
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [departments, setDepartments] = useState<{ id: number, name: string }[]>([]);

  const [editingPaidLeave, setEditingPaidLeave] = useState<number | null>(null);
  const [paidLeaveValue, setPaidLeaveValue] = useState<string>('');

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [companyConfig, setCompanyConfig] = useState({
    accounting_start_month: 1,
    accounting_start_day: 1,
    accounting_end_month: 12,
    accounting_end_day: 31,
    paid_leave_recovery_days: 5,
    allowed_negative_balance: 0
  });
  const [configLoading, setConfigLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const totalEmployees = (totalPages - 1) * 10 + employees.length;

  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/hr/departments', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data.departments);
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const fetchEmployees = async () => {
    if (!passwordVerified) return;
    try {
      setEmployeesLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '10',
        search: searchTerm,
        department: departmentFilter || ''
      });
      const response = await fetch(`/api/hr/employees?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees);
        setTotalPages(data.pagination.pages);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load employees', severity: 'error' });
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchCompanyConfig = async () => {
    try {
      setConfigLoading(true);
      const response = await fetch('/api/hr/company-config', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanyConfig(data);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load configuration', severity: 'error' });
    } finally {
      setConfigLoading(false);
    }
  };

  const handleUpdateCompanyConfig = async () => {
    try {
      setConfigLoading(true);
      const response = await fetch('/api/hr/company-config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(companyConfig)
      });
      if (response.ok) {
        const result = await response.json();
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setConfigDialogOpen(false);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update configuration', severity: 'error' });
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    if (!passwordVerified) setPasswordVerificationOpen(true);
  }, [passwordVerified]);

  useEffect(() => {
    if (passwordVerified) {
      fetchEmployees();
      fetchDepartments();
    }
  }, [page, searchTerm, departmentFilter, passwordVerified]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleDepartmentChange = (event: any) => {
    setDepartmentFilter(event.target.value);
    setPage(1);
  };

  const handleViewDetails = (userId: number) => {
    navigate(`/hr/user/${userId}`);
  };

  const handleEditPaidLeave = (employee: Employee) => {
    if (!hasCapability(user, CAP.UPDATE_PAID_LEAVE)) return;
    setEditingPaidLeave(employee.id);
    setPaidLeaveValue(employee.paid_leave_per_month?.toString() || '');
  };

  const handleSavePaidLeave = async (employeeId: number) => {
    try {
      const value = parseFloat(paidLeaveValue);
      if (isNaN(value) || value < 0) {
        setSnackbar({ open: true, message: 'Invalid value', severity: 'error' });
        return;
      }
      const response = await fetch(`/api/hr/employees/${employeeId}/paid-leave`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ paid_leave_per_month: value })
      });
      if (response.ok) {
        setSnackbar({ open: true, message: 'Updated successfully', severity: 'success' });
        setEditingPaidLeave(null);
        fetchEmployees();
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Update failed', severity: 'error' });
    }
  };

  if (!passwordVerified) {
    return (
      <PasswordVerificationModal
        open={passwordVerificationOpen}
        onClose={() => setPasswordVerificationOpen(false)}
        onVerify={async (pw) => (await authService.verifyPassword(pw)).data.verified}
        onVerified={() => { grantAccess(); setPasswordVerificationOpen(false); }}
        title="Access HR Management"
        description="Verify password to access HR features."
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean, mb: 2 }}>
        HR Management
      </Typography>

      <Paper sx={{ mb: 2, p: 2, borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <Chip
            label={`${totalEmployees} total users`}
            size="small"
            sx={{
              borderRadius: 1,
              border: `1px solid ${panelBorder}`,
              bgcolor: 'transparent',
              color: isDark ? '#b0bec5' : '#546e7a',
              fontWeight: 600,
              letterSpacing: '0.03em'
            }}
          />
          <Chip
            label={departmentFilter || 'All departments'}
            size="small"
            sx={{
              borderRadius: 1,
              border: `1px solid ${panelBorder}`,
              bgcolor: alpha(NAUTILUS.sand, isDark ? 0.12 : 0.18),
              color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              fontWeight: 600
            }}
          />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 220px auto' }, gap: 1.5, alignItems: 'center' }}>
          <TextField
            label="Search User"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Department</InputLabel>
            <Select value={departmentFilter} onChange={handleDepartmentChange} label="Department" MenuProps={getZoomCompensatedSelectMenuProps()}>
              <MenuItem value="">All Departments</MenuItem>
              {departments.map(d => <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>)}
            </Select>
          </FormControl>
          {hasCapability(user, CAP.UPDATE_PAID_LEAVE) && (
            <Button
              variant="outlined"
              startIcon={<AccountBalance />}
              onClick={() => { fetchCompanyConfig(); setConfigDialogOpen(true); }}
              sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.72rem', minHeight: 40 }}
            >
              Company Config
            </Button>
          )}
        </Box>
      </Paper>

      {employeesLoading ? <LoadingAnimation /> : (
        <Paper sx={{ borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
            <Table stickyHeader sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ position: 'sticky', top: 0, bgcolor: surfaceBg, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', borderBottom: `1px solid ${panelBorder}` }}>ID</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, bgcolor: surfaceBg, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', borderBottom: `1px solid ${panelBorder}` }}>User</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, bgcolor: surfaceBg, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', borderBottom: `1px solid ${panelBorder}` }}>Department</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, bgcolor: surfaceBg, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', borderBottom: `1px solid ${panelBorder}`, display: { xs: 'none', md: 'table-cell' } }}>Leave Balance</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, bgcolor: surfaceBg, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', borderBottom: `1px solid ${panelBorder}`, display: { xs: 'none', md: 'table-cell' } }}>R&R Balance</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, bgcolor: surfaceBg, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', borderBottom: `1px solid ${panelBorder}` }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((emp, idx) => (
                  <TableRow key={emp.id} hover sx={{ '& td': { borderBottom: `1px solid ${alpha(panelBorder, 0.65)}` }, bgcolor: idx % 2 === 0 ? 'transparent' : alpha(NAUTILUS.deepOcean, isDark ? 0.12 : 0.02) }}>
                    <TableCell>{emp.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <UserAvatar name={emp.name} src={emp.profile_picture} sx={{ width: 32, height: 32 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>{emp.name}</Typography>
                          <Typography variant="caption" sx={{ color: isDark ? '#b0bec5' : '#546e7a' }}>{emp.mail}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={emp.department || 'N/A'}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          border: `1px solid ${panelBorder}`,
                          bgcolor: alpha(NAUTILUS.deepOcean, isDark ? 0.22 : 0.04),
                          color: isDark ? '#ffffff' : NAUTILUS.deepOcean
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Chip label={`${emp.paid_leave_balance} days`} size="small" color={emp.paid_leave_balance >= 0 ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Chip label={`${emp.rnr_balance} days`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewDetails(emp.id)}
                        sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.65rem', py: 0.5, width: { xs: '100%', sm: 'auto' } }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!employees.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontWeight: 600, color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>No users found</Typography>
                        <Typography variant="body2" sx={{ color: isDark ? '#b0bec5' : '#546e7a', mt: 0.5 }}>
                          Try clearing the search or changing the department filter.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: `1px solid ${panelBorder}` }}>
            <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} shape="rounded" color="primary" />
          </Box>
        </Paper>
      )}

      {/* Config Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5, fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
          Company Holiday Configuration
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <TextField label="Start Month" type="number" fullWidth value={companyConfig.accounting_start_month}
                onChange={e => setCompanyConfig({ ...companyConfig, accounting_start_month: parseInt(e.target.value) })} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Start Day" type="number" fullWidth value={companyConfig.accounting_start_day}
                onChange={e => setCompanyConfig({ ...companyConfig, accounting_start_day: parseInt(e.target.value) })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Negative Balance Allowed" type="number" fullWidth value={companyConfig.allowed_negative_balance}
                onChange={e => setCompanyConfig({ ...companyConfig, allowed_negative_balance: parseInt(e.target.value) })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 2 }}>
          <Button onClick={() => setConfigDialogOpen(false)} sx={{ borderRadius: 1, px: 3, color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateCompanyConfig}
            sx={{ borderRadius: 1, px: 3, fontWeight: 600, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 1, border: `1px solid ${panelBorder}` }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default HRManagementPage;